<script setup lang="ts">
import { type DialogProps } from './modal'

withDefaults(defineProps<DialogProps>(), {
  size: 'md',
  outside: true
})
</script>

<template>
  <div @click.stop="" method="dialog">
    <slot v-bind="{ ...$attrs, ...$props }" />
  </div>
</template>

<style>
dialog.dialog {
  inset: 0;
  width: fit-content;
  height: fit-content;
  max-width: min(90vw, 48rem);
  max-height: calc(100% - 2em);
  padding: 0;
  margin: auto;
  overflow: visible;
  border: none;
  outline: none;
  background: transparent;
  border-radius: var(--radius-lg);
}

dialog.dialog.dialog-sm {
  max-width: min(90vw, 32rem);
}

dialog.dialog.dialog-md {
  max-width: min(90vw, 48rem);
}

dialog.dialog.dialog-lg {
  width: min(95vw, 72rem);
}

dialog.dialog.dialog-xl {
  width: min(95vw, 96rem);
}

dialog.dialog.dialog-full {
  width: 100%;
  height: 100%;
  min-width: 100%;
  min-height: 100%;
  margin: 0;
  border-radius: 0;
  padding: 0;
}

@media screen and (max-width: 998px) {
  dialog.dialog.dialog-sm,
  dialog.dialog.dialog-md,
  dialog.dialog.dialog-lg {
    max-width: 90%;
  }
}

dialog.dialog[open] {
  border: none;
  background: transparent;
  border-radius: 6px;
  opacity: 1;
  transition: opacity 300ms allow-discrete;
}

dialog.dialog[open] .dialog-content {
  transform-origin: center bottom;
  opacity: 1;
  transform: translateY(0) scale(1);
  transition:
    opacity 240ms ease-out,
    transform 360ms cubic-bezier(0.18, 0.88, 0.22, 1.1);
}

@starting-style {
  dialog.dialog[open] .dialog-content {
    opacity: 0;
    transform: translateY(72px) scale(0.95);
  }
}

@media (prefers-reduced-motion: reduce) {
  dialog.dialog[open] .dialog-content {
    transition: none;
  }
}

@starting-style {
  dialog.dialog[open] {
    opacity: 0;
  }
}

dialog.dialog[open]::backdrop {
  -webkit-backdrop-filter: blur(var(--blur-xs, 4px));
  backdrop-filter: blur(var(--blur-xs, 4px));
  background-color: rgb(0 0 0 / 25%);
}

@starting-style {
  dialog.dialog[open]::backdrop {
    background-color: rgb(0 0 0 / 0%);
  }
}

dialog.dialog::backdrop {
  -webkit-backdrop-filter: blur(var(--blur-xs, 4px));
  backdrop-filter: blur(var(--blur-xs, 4px));
  background-color: rgb(0 0 0 / 0%);
  opacity: 1;
  transition: all 300ms allow-discrete;
}

.dialog .dialog-content {
  width: 100%;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-card);
  color: var(--color-card-foreground);
  padding: 1rem;
  box-shadow: 0 10px 40px rgb(0 0 0 / 20%);
}
</style>
